// Machine display helpers — single source of truth for how a machine is
// labelled in the UI.
//
// The dashboard internally keys machines on `{company_slug}.{machine_id}`
// (a 2-part stable key that survives across the 3-part NATS scope rework
// because it doesn't depend on site).
//
// Surfacing those raw slugs to end users is hostile:
//   - "lab-001" / "home-lab.lab-001" stacked side-by-side is redundant
//   - non-admin users belong to a single company and don't need to see
//     the company prefix
//   - the human-friendly name lives in machines.name and the site name
//     lives in sites.name — both already returned by /v1/machines
//
// The shape returned here lets every consumer pick the parts it wants:
//
//   primary    — the bold label (name ?? machine_id)
//   secondary  — site name (e.g. "Potta Lab"), '' when unknown
//   tooltip    — full disambiguating slug for the title attribute
//   subtitle   — single-line "machine — location" used by tight layouts
//                that only have room for one line of identification

import { useMemo } from 'react';
import { useApiMachines } from '@/hooks/useMachines';
import { useAuthStore } from '@/store/authStore';
import type { ApiMachine } from '@/types';

export interface MachineDisplay {
  primary:   string;
  secondary: string;
  tooltip:   string;
  subtitle:  string;
}

interface Options {
  isAdmin?: boolean;
}

/** Pure resolver — takes the api machine list + dashboard key, returns
 *  display strings. Falls back to slug-stripping when the machine isn't
 *  in the api list (offline, historian-only, or pre-provision). */
export function machineDisplay(
  key: string,
  machines: ApiMachine[] | undefined,
  opts: Options = {},
): MachineDisplay {
  const segs    = key.split('.');
  const coSlug  = segs[0] ?? '';
  const midSlug = segs.slice(1).join('.') || key;

  const m = machines?.find((x) => x.company_slug === coSlug && x.machine_id === midSlug);

  const primary = (m?.name?.trim() || m?.machine_id || midSlug);
  const siteName = m?.site_name?.trim() || '';

  // Admins manage many companies, so include the company name as a third
  // axis of disambiguation; tenant users only see their own company so
  // the prefix is redundant noise.
  const secondary = opts.isAdmin && m?.company_name
    ? siteName ? `${m.company_name} · ${siteName}` : m.company_name
    : siteName;

  const subtitle = secondary ? `${primary} — ${secondary}` : primary;
  const tooltip  = m
    ? `${m.company_slug}.${m.site_slug}.${m.machine_id}`
    : key;

  return { primary, secondary, tooltip, subtitle };
}

/** React hook variant. Memoizes against the (key, machines, isAdmin) tuple. */
export function useMachineDisplay(key: string): MachineDisplay {
  const { data: machines } = useApiMachines();
  const isAdmin = useAuthStore((s) => s.user?.isAdmin ?? false);
  return useMemo(() => machineDisplay(key, machines, { isAdmin }), [key, machines, isAdmin]);
}
